import axios from "axios";

import {
  API_post_learning_file_with_eagerly_generated_presigned_url,
  API_notify_learning_file_upload_complete,
  API_get_learning_file,
  API_generate_swipe_card_image_with_ai,
} from "../../../constants";
import {
  getDefaultAxiosConfig,
  getRightworkAPI,
  postRightworkAPI,
} from "../requests/utilities/requests";

/**
 * Helpers for the swipe-deck admin authoring surface. Kept in one place
 * so the modal shell, the card editors, and the card type picker stay
 * consistent on default shapes and the upload flow.
 */

// = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = =
// LearningFile upload (matches the SectionInputsModal2 pattern)

const BYTES_PER_MB = 1024 * 1024;
export const LEARNING_IMAGE_MAX_UPLOAD_MB = 25;

// Mirrors the backend VIDEO_EXTENSIONS gate (notify_learning_file_upload_complete
// only normalizes these). Detection is by EXTENSION, deliberately NOT MIME:
//   - browsers report an empty/generic File.type for .mov/.m4v, so a MIME-only
//     check misses them (the bug this helper originally fixed), and
//   - a broad `video/*` MIME check OVER-includes formats the backend can't
//     normalize (.avi -> video/x-msvideo, .mkv -> video/x-matroska); those
//     would attach un-transcoded and fail cross-browser playback despite the
//     "optimized for all devices" promise.
// Keep this list in sync with backend utilities/video_processing.VIDEO_EXTENSIONS.
export const VIDEO_FILE_EXTENSIONS = ["mp4", "mov", "m4v", "webm"];

export function isVideoFile(file) {
  if (!file) return false;
  const name = file.name || "";
  const ext = name.includes(".") ? name.split(".").pop().toLowerCase() : "";
  return VIDEO_FILE_EXTENSIONS.includes(ext);
}
// Server-side normalization transcodes the upload to a web-compatible mp4, so
// we no longer need a tiny "keep playback acceptable" cap. Matches the backend
// reference-video ceiling (process_video_file_upload / 60-min cap).
export const LEARNING_VIDEO_MAX_UPLOAD_MB = 500;

export async function prepareLearningFileForUpload(
  file,
  { enforceVideoSizeLimit = true } = {},
) {
  if (!file) return file;

  if (isVideoFile(file)) {
    if (
      enforceVideoSizeLimit &&
      file.size > LEARNING_VIDEO_MAX_UPLOAD_MB * BYTES_PER_MB
    ) {
      throw new Error(
        `Video must be under ${LEARNING_VIDEO_MAX_UPLOAD_MB}MB. Try compressing to 720p.`,
      );
    }
    return file;
  }

  if (!file.type?.startsWith("image/")) {
    return file;
  }

  if (file.size > LEARNING_IMAGE_MAX_UPLOAD_MB * BYTES_PER_MB) {
    throw new Error(
      `Image must be under ${LEARNING_IMAGE_MAX_UPLOAD_MB}MB.`,
    );
  }
  return file;
}

// Start server-side normalization for a just-uploaded video and return its
// video_status ("pending"). Retries a transient notify/API failure (the S3
// PUT already succeeded, so we don't want to discard the upload over a blip);
// throws if it still fails so the caller surfaces an upload error instead of
// attaching a clip that never normalizes and can't be saved.
const _NOTIFY_RETRY_ATTEMPTS = 3;
async function _notifyVideoUploadComplete(companyId, fileUuid, { skipNormalization = false } = {}) {
  let lastErr;
  for (let attempt = 0; attempt < _NOTIFY_RETRY_ATTEMPTS; attempt += 1) {
    try {
      const res = await postRightworkAPI(
        API_notify_learning_file_upload_complete,
        {
          company_id: companyId,
          learning_file_uuid: fileUuid,
          // A split SOURCE must NOT be normalized (it may exceed the 10-min
          // card cap and never plays directly — only its cut segments do).
          ...(skipNormalization ? { skip_video_normalization: true } : {}),
        },
      );
      // A normal upload: notify sets video_status=pending synchronously; fall
      // back to "pending" if the response shape is missing rather than null
      // (which wouldn't poll). A skipNormalization source: no normalization
      // runs, so return the raw status (null) — the caller doesn't poll it.
      const status = res?.data?.redux?.payload?.learning_file?.video_status;
      return skipNormalization ? (status ?? null) : (status || "pending");
    } catch (e) {
      lastErr = e;
      if (attempt < _NOTIFY_RETRY_ATTEMPTS - 1) {
        await new Promise((r) => setTimeout(r, 1000 * (attempt + 1)));
      }
    }
  }
  throw new Error(
    "Couldn't start video optimization. Please try uploading again.",
    { cause: lastErr },
  );
}

/**
 * Upload a File to the LMS file pipeline. Returns `{ uuid, url }` where:
 *   - `uuid` is the LearningFile UUID, sent to the backend as
 *     `media_file_uuid` / `thumbnail_file_uuid` / `cover_file_uuid` on save
 *   - `url` is a signed S3 URL valid for ~1 hour, used for in-editor preview
 *
 * On save, only the UUID gets persisted. The signed URL is regenerated on
 * the next fetch via the deck detail serializer.
 */
export async function uploadLearningFile(
  companyId,
  file,
  { skipNormalization = false, enforceVideoSizeLimit = true } = {},
) {
  const fileToUpload = await prepareLearningFileForUpload(file, {
    enforceVideoSizeLimit,
  });
  const formData = new FormData();
  formData.append("company_id", companyId);
  formData.append("file_name", fileToUpload.name);

  const axiosConfig = await getDefaultAxiosConfig();

  // [1] Reserve a LearningFile + get a presigned PUT URL
  const reserveRes = await axios.post(
    API_post_learning_file_with_eagerly_generated_presigned_url,
    formData,
    {
      headers: {
        ...axiosConfig.headers,
        "Content-Type": "multipart/form-data",
      },
    },
  );
  const fileUuid = reserveRes?.data?.redux?.payload?.learning_file?.uuid;
  const presignedUrl = reserveRes?.data?.redux?.payload?.presigned_url;

  if (!fileUuid || !presignedUrl) {
    throw new Error("Upload reservation did not return a UUID + URL");
  }

  // [2] PUT the bytes directly to S3
  await axios.put(presignedUrl, fileToUpload, {
    headers: { "Content-Type": fileToUpload.type || "application/octet-stream" },
  });

  // [3] For VIDEO only: notify the backend the bytes have landed, which kicks
  // off normalization to a web-compatible H.264 mp4 (so iPhone .mov plays
  // everywhere) AND sets video_status=pending. Images need no post-processing,
  // so we skip the round-trip for them.
  //
  // This is NOT best-effort: the save endpoint rejects a video card unless its
  // video_status is "ready", so a swallowed notify failure would leave an
  // attached-but-unsaveable clip that never polls or retries. Retry the small
  // notify call to ride out a transient blip; if it still fails, throw so the
  // caller surfaces it as an upload failure (and doesn't attach a dead clip)
  // rather than stranding the author.
  let videoStatus = null;
  if (isVideoFile(fileToUpload)) {
    videoStatus = await _notifyVideoUploadComplete(companyId, fileUuid, { skipNormalization });
  }

  // [4] Resolve the signed read URL for in-editor preview
  const fetchRes = await getRightworkAPI(API_get_learning_file, {
    company_id: companyId,
    learning_file_uuid: fileUuid,
  });
  const fileUrl =
    fetchRes?.data?.redux?.payload?.learning_file?.file || null;

  return { uuid: fileUuid, url: fileUrl, videoStatus };
}

/**
 * Generate an AI image for a card from its media suggestion. Returns
 * `{ uuid, url, mediaSource }` — uuid goes to `media_file_uuid` on save,
 * the signed URL drives the in-editor preview, and mediaSource is
 * "document" when the backend reused a real photo from `fileUuids` (a
 * strong caption match) or "generated" otherwise. Synchronous on the
 * server (15-30s render, or instant on a photo match), so callers
 * should show a generating state and cap concurrency. Throws on failure.
 */
export async function generateSwipeCardImage(
  companyId,
  mediaSuggestion,
  contextName,
  fileUuids = [],
  excludeSha1s = [],
  allowGeneration = true,
  headline = "",
) {
  const res = await postRightworkAPI(API_generate_swipe_card_image_with_ai, {
    company_id: companyId,
    media_suggestion: mediaSuggestion,
    context_name: contextName || "",
    // The card's headline (dish/wine name) — lets the backend's vision
    // tiebreaker place the right real photo when candidates cluster. Empty
    // for cards without a headline (quiz/poll), which falls back to top match.
    headline: headline || "",
    // When present, the backend tries a real-photo match against these
    // documents before generating (parity with the studio fill).
    file_upload_uuids: JSON.stringify(fileUuids || []),
    // Content hashes already placed in this deck — the backend skips a
    // real-photo match with one of these so the same picture (or a
    // byte-identical copy) isn't reused on another card.
    exclude_sha1s: JSON.stringify(excludeSha1s || []),
    // "Photos only" mode: reuse a real photo but don't AI-generate. The
    // backend returns a null file when nothing matches.
    allow_generation: allowGeneration,
  });
  const payload = res?.data?.redux?.payload;
  const learningFile = payload?.learning_file;
  if (!learningFile?.uuid) {
    // No file: either a genuine miss in "photos only" mode (expected — the
    // caller leaves the card unillustrated) or a generation failure. When
    // generation was allowed, surface it so the caller's catch can react.
    if (allowGeneration) {
      throw new Error("Image generation did not return a file");
    }
    return { uuid: null, url: null, mediaSource: null, layoutVariant: null, sha1: null };
  }
  return {
    uuid: learningFile.uuid,
    url: learningFile.url || null,
    mediaSource: payload?.media_source || "generated",
    // Aspect-appropriate layout the backend picked (landscape document
    // photo → "split", else "full_bleed"). Callers apply it to the card.
    layoutVariant: payload?.layout_variant || "full_bleed",
    // Content hash of the reused photo (null when generated). The caller
    // accumulates these to exclude on subsequent cards.
    sha1: payload?.sha1 || null,
  };
}

// = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = =
// Default-card factories per card type

const DEFAULT_DECK = {
  uuid: null,
  name: "",
  name_spanish: "",
  name_chinese: "",
  description: "",
  description_spanish: "",
  description_chinese: "",
  cover_file_uuid: null,
  cover_url: null,
  cards: [],
};

export function makeDefaultDeck() {
  return JSON.parse(JSON.stringify(DEFAULT_DECK));
}

const CARD_DEFAULTS = {
  image_text: {
    card_type: "image_text",
    layout_variant: "full_bleed",
    content_json: { headline: "", body: "", emoji: null },
    metadata: {},
    media_file_uuid: null,
    media_url: null,
    thumbnail_file_uuid: null,
    thumbnail_url: null,
  },
  video: {
    card_type: "video",
    // full_bleed (crop-to-fill) by default; the editor can switch to split
    // to show a landscape clip's whole frame. Mirrors image_text.
    layout_variant: "full_bleed",
    content_json: { title: "", caption: "", captions_burned_in: false },
    metadata: {},
    media_file_uuid: null,
    media_url: null,
    thumbnail_file_uuid: null,
    thumbnail_url: null,
  },
  poll: {
    card_type: "poll",
    layout_variant: null,
    content_json: {
      question: "",
      choices: [
        { text: "", emoji: null },
        { text: "", emoji: null },
      ],
      show_percentages: true,
    },
    metadata: {},
    media_file_uuid: null,
    media_url: null,
    thumbnail_file_uuid: null,
    thumbnail_url: null,
  },
  quiz: {
    card_type: "quiz",
    layout_variant: null,
    content_json: {
      question: "",
      choices: [
        { text: "", is_correct: false },
        { text: "", is_correct: false },
      ],
      explanation: null,
      shuffle_choices: true,
    },
    metadata: {},
    media_file_uuid: null,
    media_url: null,
    thumbnail_file_uuid: null,
    thumbnail_url: null,
  },
};

export function makeDefaultCard(cardType) {
  const template = CARD_DEFAULTS[cardType];
  if (!template) {
    throw new Error(`Unknown card_type: ${cardType}`);
  }
  // Deep clone so mutations don't leak between cards
  return {
    uuid: null,
    // Stable identity for unsaved cards — needed as a draggableId for
    // @hello-pangea/dnd, which requires a unique key that survives
    // reorders. New cards have uuid: null so we generate one client-side.
    _clientId: window.crypto.randomUUID(),
    order_index: 0,
    ...JSON.parse(JSON.stringify(template)),
  };
}

// = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = =
// Save payload preparation

/**
 * Strip transient FE-only fields (media_url / thumbnail_url / cover_url)
 * from a deck's state before posting. The backend resolves those from
 * UUIDs and returns fresh signed URLs in the response.
 */
export function buildDeckSavePayload(deck) {
  // Note: content_json_spanish / content_json_chinese are intentionally
  // omitted. The editor doesn't author translations — the BE Celery
  // task owns them. Including them in the payload would race against
  // in-flight retranslations: a fresh save would re-send the stale
  // translation that was loaded with the deck, overwriting whatever
  // the previous save's Celery task just produced. The BE upsert
  // treats absence of the key as "preserve existing value".
  const cards = (deck.cards || []).map((card, idx) => ({
    uuid: card.uuid || undefined,
    order_index: idx,
    card_type: card.card_type,
    layout_variant: card.layout_variant || null,
    content_json: card.content_json || {},
    metadata: card.metadata || {},
    media_file_uuid: card.media_file_uuid || null,
    thumbnail_file_uuid: card.thumbnail_file_uuid || null,
  }));
  return {
    learning_swipe_deck_uuid: deck.uuid || undefined,
    name: deck.name || "",
    name_spanish: deck.name_spanish || "",
    name_chinese: deck.name_chinese || "",
    description: deck.description || "",
    description_spanish: deck.description_spanish || "",
    description_chinese: deck.description_chinese || "",
    cover_file_uuid: deck.cover_file_uuid || "",
    cards: JSON.stringify(cards),
  };
}

/**
 * Convert an editor-shaped deck (FE local state) back into a BE-response
 * shape so the player can render it directly without a server round-trip.
 *
 * Used when an admin clicks Preview from inside the editor: the deck
 * exists only in editor state (possibly with unsaved cards that have no
 * server-issued UUID). We synthesize temp UUIDs and reshape `content_json`
 * → `content`, `media_file_uuid` + `media_url` → `media: {uuid, url}`,
 * etc., to match what `LearningSwipeDeckDetailGetSerializer` produces.
 *
 * Quiz answer keys (the `is_correct` flag on each choice) are preserved
 * in the output — preview needs them so it can locally compute correctness
 * without a server submit. The real player in employee mode never sees
 * answer keys because the BE serializer strips them by default.
 */
export function denormalizeEditorDeckForPreview(deck) {
  if (!deck) return null;
  return {
    uuid: deck.uuid || "preview-deck",
    name: deck.name || "",
    name_spanish: deck.name_spanish || null,
    name_chinese: deck.name_chinese || null,
    description: deck.description || "",
    cover: deck.cover_file_uuid
      ? { uuid: deck.cover_file_uuid, url: deck.cover_url || null }
      : null,
    cards: (deck.cards || []).map((c, idx) => ({
      uuid: c.uuid || `preview-card-${idx}`,
      order_index: idx,
      card_type: c.card_type,
      layout_variant: c.layout_variant || null,
      content: c.content_json || {},
      metadata: c.metadata || {},
      media: c.media_file_uuid
        ? { uuid: c.media_file_uuid, url: c.media_url || null }
        : null,
      thumbnail: c.thumbnail_file_uuid
        ? { uuid: c.thumbnail_file_uuid, url: c.thumbnail_url || null }
        : null,
    })),
  };
}

/**
 * Convert a backend-shaped deck (from get_learning_swipe_deck_detail) into
 * the FE editor state shape — flattens `cover.url` into `cover_url`,
 * `media.url` into `media_url`, etc., so editors can render previews
 * without going through the BE shape.
 */
export function normalizeBackendDeck(beDeck) {
  if (!beDeck) return makeDefaultDeck();
  return {
    uuid: beDeck.uuid || null,
    name: beDeck.name || "",
    name_spanish: beDeck.name_spanish || "",
    name_chinese: beDeck.name_chinese || "",
    description: beDeck.description || "",
    description_spanish: beDeck.description_spanish || "",
    description_chinese: beDeck.description_chinese || "",
    cover_file_uuid: beDeck.cover?.uuid || null,
    cover_url: beDeck.cover?.url || null,
    cards: (beDeck.cards || []).map((c) => ({
      uuid: c.uuid,
      _clientId: c.uuid || window.crypto.randomUUID(),
      order_index: c.order_index,
      card_type: c.card_type,
      layout_variant: c.layout_variant || null,
      content_json: c.content || {},
      content_json_spanish: c.content_spanish || null,
      content_json_chinese: c.content_chinese || null,
      metadata: c.metadata || {},
      media_file_uuid: c.media?.uuid || null,
      media_url: c.media?.url || null,
      thumbnail_file_uuid: c.thumbnail?.uuid || null,
      thumbnail_url: c.thumbnail?.url || null,
    })),
  };
}
